import React, { useState, useEffect } from 'react';
import { Info, AlertTriangle, CheckCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const AppFooter = () => {
  const [versionInfo, setVersionInfo] = useState({
    frontend: null,
    backend: null,
    loading: true,
    error: null,
    synced: true
  });

  useEffect(() => {
    const fetchVersions = async () => {
      let frontendData = null;
      let backendData = null;

      // Obtener versión del frontend (archivo estático)
      try {
        const frontendRes = await fetch('/version.json?t=' + Date.now());
        if (frontendRes.ok) {
          frontendData = await frontendRes.json();
        }
      } catch (e) {
        console.warn('No se pudo obtener version.json del frontend:', e);
      }

      // Obtener versión del backend
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const backendRes = await fetch(`${API_URL}/api/version`);
        if (backendRes.ok) {
          backendData = await backendRes.json();
        }
      } catch (e) {
        console.warn('No se pudo obtener /api/version del backend:', e);
      }

      // Determinar si están sincronizados
      const synced = frontendData && backendData 
        ? frontendData.version === backendData.version 
        : true; // Si falta uno, no mostrar warning

      setVersionInfo({
        frontend: frontendData,
        backend: backendData,
        loading: false,
        error: (!frontendData && !backendData) ? 'No se pudo obtener la versión' : null,
        synced
      });
    };

    fetchVersions();
  }, []);

  if (versionInfo.loading) {
    return (
      <footer className="bg-gray-100 border-t border-gray-200 px-4 py-2">
        <div className="flex items-center justify-center text-xs text-gray-400">
          <span>Cargando versión...</span>
        </div>
      </footer>
    );
  }

  const version = versionInfo.frontend?.version || versionInfo.backend?.version || '?';
  const buildDate = versionInfo.frontend?.build_date || versionInfo.backend?.build_date;

  return (
    <footer className="bg-gray-100 border-t border-gray-200 px-4 py-2">
      <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
        <div className="text-xs text-gray-500">
          © {new Date().getFullYear()} LexDuo
        </div>
        
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-xs cursor-help">
                {!versionInfo.synced ? (
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                ) : (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                )}
                <span className={`font-mono ${!versionInfo.synced ? 'text-amber-600' : 'text-gray-600'}`}>
                  v{version}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <div className="space-y-1">
                <p className="font-semibold">Información de Versión</p>
                <p>Frontend: v{versionInfo.frontend?.version || '?'}</p>
                <p>Backend: v{versionInfo.backend?.version || '?'}</p>
                {buildDate && (
                  <p className="text-gray-400">
                    Build: {new Date(buildDate).toLocaleDateString('es-ES')}
                  </p>
                )}
                {!versionInfo.synced && (
                  <p className="text-amber-500 font-medium mt-1">
                    ⚠️ Versiones desincronizadas
                  </p>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </footer>
  );
};

export default AppFooter;
